import { graphql, Link } from 'gatsby';
import Image from 'gatsby-image';
import React from 'react';
import { Seo } from '../components/seo';
import styles from './index.module.scss';
import { useLocalize } from '../components/localize-context';
import { PackagesAndComboBookingForm } from '../components/packages-and-combo-booking-form';
import { trackEvent } from '../analytics';

export default function Home({
  data: { homepage, allPackages, schedules, combo },
}) {
  const localize = useLocalize();

  return (
    <>
      <Seo title={homepage.data.title.text} />
      <section className="container mx-auto max-w-3xl py-6 px-4">
        <PackagesAndComboBookingForm
          packages={allPackages.nodes}
          schedules={schedules.nodes}
          combo={combo.data}
        />
      </section>
      {homepage.data.body.map(({ primary: { position, service } }) => {
        const svc = service.document[0];
        const serviceDetails = svc.data;
        const onImageLoad = () =>
          trackEvent({
            category: 'image',
            action: 'load',
            label: `load image ${svc.uid}`,
            nonInteraction: true,
          });
        return (
          <div className={`relative ${styles.section}`} key={service.id}>
            <div className="hidden xs:block">
              <Image
                fluid={serviceDetails.image.localFile.childImageSharp.fluid}
                alt={serviceDetails.image.alt}
                onLoad={onImageLoad}
              />
            </div>
            <div className="xs:hidden">
              <Image
                fluid={
                  serviceDetails.image.vertical
                    ? serviceDetails.image.vertical.localFile.childImageSharp
                        .fluid
                    : serviceDetails.image.localFile.childImageSharp.fluid
                }
                alt={serviceDetails.image.alt}
                onLoad={onImageLoad}
              />
            </div>
            <div
              className={`absolute max-w-lg p-3 ${
                position === 'bottom-left'
                  ? styles.labelLeft
                  : position === 'top-left'
                  ? styles.labelTopLeft
                  : position === 'top-right'
                  ? styles.labelTopRight
                  : styles.labelRight
              }`}
            >
              <h2
                className={`text-3xl md:text-5xl text-blue-700 px-4 inline-block rounded-t-lg leading-normal ${styles.title}`}
              >
                {serviceDetails.name.text}
              </h2>
              <div
                className={`sm:text-lg leading-relaxed py-2 px-4 rounded-b-lg ${styles.desc} shadow text-justify`}
              >
                <div
                  className="rich-text"
                  dangerouslySetInnerHTML={{
                    __html: serviceDetails.description.html,
                  }}
                />
                {serviceDetails.customPage && (
                  <div className="text-right">
                    <Link
                      to={serviceDetails.customPage.url}
                      className="text-blue-500"
                    >
                      {'>'} {localize('Learn More')}
                    </Link>
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })}
      <section className="container mx-auto max-w-3xl py-6 px-4">
        <PackagesAndComboBookingForm
          packages={allPackages.nodes}
          schedules={schedules.nodes}
          combo={combo.data}
        />
      </section>
    </>
  );
}

export const pageQuery = graphql`
  query HomeQuery($lang: String!) {
    homepage: prismicHomepage(lang: { eq: $lang }) {
      data {
        title {
          text
        }
        body {
          primary {
            position: description_position
            service {
              id
              document {
                uid
                data {
                  name {
                    text
                  }
                  description {
                    html
                  }
                  customPage: custom_page {
                    url
                  }
                  image: main_image {
                    url
                    alt
                    localFile {
                      childImageSharp {
                        fluid(maxWidth: 1500, quality: 100) {
                          ...GatsbyImageSharpFluid_withWebp
                        }
                      }
                    }
                    vertical {
                      localFile {
                        childImageSharp {
                          fluid(maxWidth: 450, quality: 100) {
                            ...GatsbyImageSharpFluid_withWebp
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
    allPackages: allPrismicPackage(
      sort: { fields: data___order }
      filter: { lang: { eq: $lang } }
    ) {
      nodes {
        uid
        data {
          name {
            text
          }
          includedServices: included_services {
            service {
              document {
                data {
                  name {
                    text
                  }
                }
              }
            }
          }
          additionalServices: additional_services {
            text {
              text
            }
          }
          localAdultPrice: unit_price__local_adult_
          localKidPrice: unit_price__local_kid_
          foreignAdultPrice: unit_price__international_adult_
          foreignKidPrice: unit_price__international_kid_
        }
      }
    }
    schedules: allScheduleYaml(filter: { isFuture: { eq: true } }) {
      nodes {
        date(formatString: "YYYY-MM-DD")
        time
      }
    }
    combo: prismicServiceCombo(lang: { eq: $lang }) {
      ...ComboBookingForm
    }
  }
`;
